import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSupplierCrmOrgStore } from '../../../store/supplierCrmOrg.store';
import { fetchSupplierDashboardSummary, type SupplierDashboardSummary } from '../../../services/supplierCrm/dashboardService';

const SupplierHubHeader: React.FC = () => {
  const { activeOrgId } = useSupplierCrmOrgStore();
  const [summary, setSummary] = useState<SupplierDashboardSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    if (!activeOrgId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSupplierDashboardSummary(activeOrgId);
      setSummary(data);
    } catch (err: any) {
      console.error('Failed to fetch supplier dashboard summary:', err);
      setSummary(null);
      setError(err?.message || 'Failed to load supplier metrics');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId]);

  useEffect(() => {
    if (!activeOrgId) return;
    void loadSummary();
  }, [activeOrgId, loadSummary]);

  const cards = useMemo(() => {
    const totals = summary?.totals;
    const pipeline = summary?.pipeline;
    const formatCurrency = (value: number) => {
      if (value >= 1000) {
        return `£${(value / 1000).toFixed(1)}k`;
      }
      return `£${new Intl.NumberFormat().format(value)}`;
    };

    return [
      {
        label: 'Total Suppliers',
        value: totals?.suppliers ?? 0,
        icon: '🏢'
      },
      {
        label: 'Active Suppliers',
        value: totals?.activeSuppliers ?? 0,
        icon: '✅'
      },
      {
        label: 'Contacts',
        value: totals?.contacts ?? 0,
        icon: '👥'
      },
      {
        label: 'Quotes',
        value: totals?.quotes ?? 0,
        icon: '💰'
      },
      {
        label: 'Pipeline Value',
        value: pipeline ? formatCurrency(pipeline.openValue) : '—',
        icon: '📈',
        isValue: true
      }
    ];
  }, [summary]);

  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
      {/* Title */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Supplier Operations Hub</h1>
          <Link
            to="/help?article=supplier-operations-navigation"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
          >
            ❓ Help
          </Link>
        </div>
        <p className="mt-1 text-sm text-gray-600">
          Unified workspace for supplier discovery, relationship management, and operations
        </p>
      </div>

      {error ? (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <div className="flex items-center justify-between gap-4">
            <span>{error}</span>
            <button
              type="button"
              className="text-primary underline"
              onClick={() => {
                if (!loading) {
                  void loadSummary();
                }
              }}
            >
              Retry
            </button>
          </div>
        </div>
      ) : null}

      {/* Metrics Dashboard */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        {cards.map((card) => (
          <MetricCard
            key={card.label}
            label={card.label}
            value={card.value}
            icon={card.icon}
            loading={loading}
            isValue={card.isValue}
          />
        ))}
      </div>
    </div>
  );
};

interface MetricCardProps {
  label: string;
  value: number | string;
  icon: string;
  loading: boolean;
  isValue?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, icon, loading, isValue = false }) => {
  return (
    <div className="rounded-xl bg-gray-50 p-4">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
          <p className="mt-2 text-2xl font-bold text-gray-900">
            {loading ? (
              <span className="inline-block h-8 w-16 animate-pulse rounded bg-gray-200"></span>
            ) : isValue ? (
              value
            ) : (
              new Intl.NumberFormat().format(value as number)
            )}
          </p>
        </div>
        <div className="text-2xl">{icon}</div>
      </div>
    </div>
  );
};

export default SupplierHubHeader;
